/** Shared constants and helpers for Prosperity Mentors CRM + public URLs. */

import { sessionTimeMergeFields } from './pmEventTime';

export const PM_MANAGE_ROLES = ['associate', 'setter', 'closer', 'manager', 'admin', 'super_admin'];
export const PM_ADMIN_BYPASS_ROLES = ['admin', 'super_admin'];
/** Matches pm_event_invitation_links RLS — managers may create/view invites for others. */
export const PM_INVITE_ADMIN_ROLES = ['manager', 'admin', 'super_admin'];

export const PM_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

export const PM_LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'bilingual', label: 'Bilingual' },
];

export const PM_FORMAT_OPTIONS = [
  { value: 'in_person', label: 'In person' },
  { value: 'online', label: 'Online' },
];

export const PM_SPEAKER_ROLE_OPTIONS = [
  { value: 'speaker', label: 'Hosted by' },
  { value: 'special_guest', label: 'Guest speaker' },
  { value: 'featured_leader', label: 'Featured leader' },
  { value: 'guest', label: 'Guest (unassigned)' },
];

/** Roles shown on Fintech Leaders landings (excludes legacy `guest`). */
export const PM_SPEAKER_LANDING_ROLES = [
  { value: 'speaker', label: 'Hosted by' },
  { value: 'special_guest', label: 'Guest speakers' },
  { value: 'featured_leader', label: 'Featured leaders' },
];

export const PM_LANDING_TEMPLATES = [
  {
    value: 'classic',
    label: 'Classic',
    description: 'Balanced layout — hero, speakers, sessions, and registration',
  },
  {
    value: 'spotlight',
    label: 'Spotlight',
    description: 'Speaker-forward, best when your host is the draw',
  },
  {
    value: 'convert',
    label: 'Convert',
    description: 'Form-forward, best for paid or high-intent events',
  },
  {
    value: 'fintech_leaders',
    label: 'Fintech Leaders',
    description: 'Partners + featured leaders layout for sponsor-forward events',
  },
  {
    value: 'finplan',
    label: 'FinPlan Template',
    description: 'Class series with hosts, speakers, and optional landing sections',
  },
];

/** Unknown or empty template keys render as Classic. */
export function resolveLandingTemplateId(id) {
  const key = String(id || '').trim();
  return PM_LANDING_TEMPLATES.some((template) => template.value === key) ? key : 'classic';
}

/** Printed under testimonial sections when the editor creates one with an empty disclaimer. */
export const PM_TESTIMONIAL_DISCLAIMER_DEFAULT =
  'Results shown are not typical and depend on individual effort, licensing and market conditions. No income is guaranteed.';

/**
 * Landing-section registry. A new section type is a new entry here plus a
 * website renderer — the editor builds its cards from this list.
 * Item fields: key, label, required, max, input ('text' | 'textarea' | 'photo').
 */
export const PM_LANDING_SECTION_TYPES = [
  {
    key: 'learn',
    label: "What you'll learn",
    defaultHeading: "What you'll learn",
    maxItems: 7,
    fields: [
      { key: 'title', label: 'Title', required: true, max: 80, input: 'text' },
      { key: 'description', label: 'Description', required: false, max: 200, input: 'textarea' },
    ],
  },
  {
    key: 'testimonials',
    label: 'Testimonials',
    defaultHeading: 'Testimonials',
    maxItems: 6,
    disclaimer: {
      label: 'Disclaimer',
      max: 300,
      defaultText: PM_TESTIMONIAL_DISCLAIMER_DEFAULT,
    },
    fields: [
      { key: 'quote', label: 'Quote', required: true, max: 300, input: 'textarea' },
      { key: 'name', label: 'Name', required: true, max: 60, input: 'text' },
      { key: 'role', label: 'Role', required: false, max: 60, input: 'text' },
      { key: 'photo_url', label: 'Photo', required: false, input: 'photo' },
    ],
  },
];

export function landingSectionType(key) {
  return PM_LANDING_SECTION_TYPES.find((type) => type.key === key) || null;
}

export function newLandingSectionId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `sec_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function newLandingSection(typeKey) {
  const type = landingSectionType(typeKey);
  if (!type) return null;
  const section = {
    id: newLandingSectionId(),
    type: type.key,
    enabled: true,
    heading: '',
    items: [],
  };
  if (type.disclaimer) section.disclaimer = type.disclaimer.defaultText;
  return section;
}

export function newLandingItem(typeKey) {
  const type = landingSectionType(typeKey);
  if (!type) return {};
  const item = {};
  for (const field of type.fields) item[field.key] = '';
  return item;
}

function clampSectionText(value, max) {
  const text = String(value ?? '').trim();
  return [...text].slice(0, max).join('');
}

function landingItemIsEmpty(item, type) {
  return type.fields.every((field) => !String(item?.[field.key] ?? '').trim());
}

/**
 * Trim section copy, drop empty items, and reject incomplete required fields.
 * Unknown types are kept so a newer registry entry is not erased by this editor.
 * Returns { sections, error }.
 */
export function prepareLandingSections(raw) {
  if (!Array.isArray(raw)) return { sections: [], error: null };
  const sections = [];
  const seen = new Set();
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const type = landingSectionType(entry.type);
    if (!type) {
      sections.push(entry);
      if (sections.length > 10) {
        return { sections: null, error: 'Landing pages allow up to 10 sections.' };
      }
      continue;
    }
    if (seen.has(type.key)) {
      return { sections: null, error: `Only one ${type.label} section is allowed.` };
    }
    seen.add(type.key);
    const items = [];
    const rawItems = Array.isArray(entry.items) ? entry.items : [];
    for (const rawItem of rawItems) {
      if (landingItemIsEmpty(rawItem, type)) continue;
      const item = {};
      for (const field of type.fields) {
        if (field.input === 'photo') {
          const url = String(rawItem?.[field.key] ?? '').trim();
          if (url) item[field.key] = url;
          continue;
        }
        const text = clampSectionText(rawItem?.[field.key], field.max);
        if (field.required && !text) {
          return {
            sections: null,
            error: `${type.label}: ${field.label} is required (max ${field.max} characters).`,
          };
        }
        if (text) item[field.key] = text;
      }
      items.push(item);
      if (items.length > type.maxItems) {
        return { sections: null, error: `${type.label} allows up to ${type.maxItems} items.` };
      }
    }
    const section = {
      id: String(entry.id || '').trim() || newLandingSectionId(),
      type: type.key,
      enabled: entry.enabled !== false,
      heading: clampSectionText(entry.heading, 80),
      items,
    };
    if (type.disclaimer) {
      section.disclaimer = clampSectionText(entry.disclaimer, type.disclaimer.max);
    }
    sections.push(section);
    if (sections.length > 10) {
      return { sections: null, error: 'Landing pages allow up to 10 sections.' };
    }
  }
  return { sections, error: null };
}

/**
 * Default multi-event alias host for Mentors landings/invites when an event has
 * no dedicated custom_domain. Path is /{eventSlug} (no /events/ prefix).
 * Keep in sync manually with MULTI_EVENT_HOSTS in the website repo's
 * src/lib/requestHost.ts and PM_MULTI_EVENT_ALIAS_HOSTS in this repo's
 * supabase/functions/_shared/cors.ts — three places now hardcode this domain.
 */
export const PM_PUBLIC_EVENTS_BASE = 'https://finplan.club';

/**
 * Normalize optional custom domain to bare hostname (lowercase, no protocol/www/path).
 * Returns null for empty/invalid input.
 */
export function normalizeCustomDomain(value) {
  let s = String(value ?? '').trim().toLowerCase();
  if (!s) return null;
  s = s.replace(/^https?:\/\//i, '');
  s = s.split('/')[0]?.split('?')[0]?.split('#')[0] ?? '';
  s = s.replace(/:\d+$/, '');
  s = s.replace(/\.$/, '');
  if (s.startsWith('www.')) s = s.slice(4);
  s = s.trim();
  if (!s) return null;
  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(s)) {
    return null;
  }
  return s;
}

/** True when value is empty or a valid bare hostname (after normalization). */
export function isValidCustomDomainInput(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return true;
  return Boolean(normalizeCustomDomain(raw));
}

export function getPmCustomDomainUrl(customDomain) {
  const host = normalizeCustomDomain(customDomain);
  return host ? `https://${host}` : null;
}

export function canManagePmEvents(user) {
  if (!user) return false;
  if (PM_ADMIN_BYPASS_ROLES.includes(user.role)) return true;
  return PM_MANAGE_ROLES.includes(user.role) && user.is_licensed === true;
}

export function canAdminPmInvites(user) {
  if (!user) return false;
  return PM_INVITE_ADMIN_ROLES.includes(user.role);
}

export function slugify(text) {
  return String(text || '')
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

/**
 * Default-host public live URL (finplan.club multi-event alias).
 * Landing: /{eventSlug}. Invite: /{eventSlug}?invite={inviteSlug}.
 * First path segment is always the event slug — never a bare invite code.
 * For dedicated custom_domain URLs, use getPmEventPreferredLiveUrl / getPmCustomDomainUrl.
 */
export function getPmEventLiveUrl(eventSlug, inviteSlug = null) {
  if (!eventSlug) return null;
  const base = `${PM_PUBLIC_EVENTS_BASE}/${eventSlug}`;
  if (inviteSlug) return `${base}?invite=${encodeURIComponent(inviteSlug)}`;
  return base;
}

/**
 * Preferred public URL: dedicated custom_domain when set, else finplan.club default.
 * Custom domain landing = https://{domain}; invite = https://{domain}/{inviteSlug}
 * (bare invite path — single-event host). Default host always uses ?invite=.
 */
export function getPmEventPreferredLiveUrl(event, inviteSlug = null) {
  const host = normalizeCustomDomain(event?.custom_domain);
  if (host) {
    if (inviteSlug) return `https://${host}/${inviteSlug}`;
    return `https://${host}`;
  }
  return getPmEventLiveUrl(event?.slug, inviteSlug);
}

/** Invitation share URL — same page as the event, invite slug as query param. */
export function getPmInviteUrl(eventSlug, inviteSlug) {
  if (!eventSlug || !inviteSlug) return null;
  return getPmEventLiveUrl(eventSlug, inviteSlug);
}

export function getStatusBadgeVariant(status) {
  switch (status) {
    case 'published':
      return 'success';
    case 'archived':
      return 'default';
    default:
      return 'warning';
  }
}

export function defaultEventPayload(overrides = {}) {
  return {
    title: '',
    short_description: '',
    slug: '',
    custom_domain: '',
    language: 'en',
    format: 'in_person',
    requires_payment: false,
    price: null,
    early_bird_price: '',
    early_bird_deadline: '',
    late_price: '',
    is_main_event: false,
    landing_template_id: 'classic',
    landing_headline: '',
    landing_badge: '',
    registration_scope: 'single_session',
    landing_sections: [],
    theme: 'default',
    density: 'comfortable',
    hero_style: 'standard',
    hero_image_url: '',
    allow_self_serve_guests: false,
    status: 'draft',
    pipeline_event_type: null,
    agent_id: null,
    marketing_director_id: null,
    business_launch_assignee_name: '',
    business_launch_assignee_phone: '',
    business_launch_assignee_email: '',
    ...overrides,
  };
}

/** Postgres numeric arrives as a string; '' and non-numeric both mean "unset". */
export function toPmPriceNumber(value) {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Which price a registrant is charged right now.
 * Must stay in sync with resolveTier() in supabase/functions/create-pm-event-checkout.
 */
export function resolvePmEventPrice(event) {
  const earlyBird = toPmPriceNumber(event?.early_bird_price);
  const deadline = event?.early_bird_deadline ? Date.parse(event.early_bird_deadline) : NaN;
  if (earlyBird !== null && Number.isFinite(deadline) && Date.now() <= deadline) {
    return { tier: 'early_bird', amount: earlyBird };
  }
  const late = toPmPriceNumber(event?.late_price);
  return { tier: 'late', amount: late !== null ? late : toPmPriceNumber(event?.price) };
}

export function formatPmPrice(amount) {
  return typeof amount === 'number' && Number.isFinite(amount) ? `$${amount.toFixed(2)}` : null;
}

export const PM_PAYMENT_TIER_LABELS = {
  early_bird: 'Early bird',
  late: 'Late',
};

/**
 * Display name/contact for a pm_events assignee.
 * Prefers linked CRM user (agent embed or agent_* fields); falls back to
 * business_launch_assignee_* when agent_id is null ("not in the system yet").
 */
export function getPmEventAssigneeDisplay(event) {
  if (!event) return { name: null, phone: null, email: null, source: null };

  const agent = event.agent || null;
  const agentName = agent?.full_name?.trim() || agent?.email?.trim() || null;
  if (agentName || event.agent_id) {
    return {
      name: agentName,
      phone: null,
      email: agent?.email?.trim() || null,
      source: 'agent',
    };
  }

  const manualName = String(event.business_launch_assignee_name || '').trim() || null;
  const manualEmail = String(event.business_launch_assignee_email || '').trim() || null;
  const manualPhone = String(event.business_launch_assignee_phone || '').trim() || null;
  if (manualName || manualEmail || manualPhone) {
    return {
      name: manualName || manualEmail,
      phone: manualPhone,
      email: manualEmail,
      source: 'manual',
    };
  }

  return { name: null, phone: null, email: null, source: null };
}

const PREVIEW_DEFAULT_TZ = 'America/New_York';
/** Non-merge-field key carrying the {{session_time}} HTML / plain-text body blocks. */
const PREVIEW_SESSION_TIME_BLOCKS = '__sessionTimeBlocks';
const INVITED_BY_START = '<!--INVITED_BY_START-->';
const INVITED_BY_END = '<!--INVITED_BY_END-->';
const MD_START = '<!--MD_START-->';
const MD_END = '<!--MD_END-->';

/** Same ui-avatars fallback the confirmation/reminder Edge Functions use. */
export function fallbackInviterHeadshotUrl(fullName) {
  const name = String(fullName || '').trim() || '?';
  return (
    `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}` +
    '&background=CBD5E1&color=475569&size=128'
  );
}

function formatPreviewSessionDate(startsAt, timeZone) {
  try {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: timeZone || PREVIEW_DEFAULT_TZ,
    }).format(new Date(startsAt));
  } catch {
    return 'Aug 20, 2026';
  }
}

function formatPreviewSessionTime(startsAt, timeZone) {
  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timeZone || PREVIEW_DEFAULT_TZ,
      timeZoneName: 'short',
    }).format(new Date(startsAt));
  } catch {
    return '7:00 PM';
  }
}

/**
 * {{paid_session_details}} sample — only requires_payment sessions, matching what
 * stripe-pm-webhook sends after a successful payment. One line per session; the
 * HTML preview path turns the newlines into <br>.
 */
function buildPreviewPaidSessionDetails(sessions, fallbackLocation) {
  const paid = (Array.isArray(sessions) ? sessions : []).filter((s) => s?.requires_payment);
  if (!paid.length) {
    return `Session 1 — ${formatPreviewSessionDate(Date.now(), PREVIEW_DEFAULT_TZ)} — ${fallbackLocation}`;
  }
  return paid
    .map((s) => {
      const tz = s.timezone?.trim() || PREVIEW_DEFAULT_TZ;
      const parts = [];
      if (s.title) parts.push(s.title);
      if (s.starts_at) {
        parts.push(formatPreviewSessionDate(s.starts_at, tz), formatPreviewSessionTime(s.starts_at, tz));
      }
      const where = String(s.meeting_url || '').trim() || s.location_text || '';
      if (where) parts.push(where);
      return parts.join(' — ');
    })
    .join('\n');
}

/** Placeholder session for previews of events that have no sessions yet. */
const PREVIEW_SAMPLE_SESSION = {
  starts_at: '2026-08-20T23:00:00Z',
  ends_at: '2026-08-21T01:00:00Z',
  timezone: PREVIEW_DEFAULT_TZ,
};

/**
 * Sample merge values for the Mentors Emails-tab live preview.
 * Uses the event title + first session when present; otherwise placeholders.
 * multiZone (default) mirrors confirmation / reminder / custom sends, where {{session_time}}
 * is the multi-timezone block; pass false for the paid template (stripe-pm-webhook still
 * renders a single-zone start time).
 */
export function buildPreviewMergeValues(event, sessions, { multiZone = true } = {}) {
  const first = Array.isArray(sessions) && sessions.length ? sessions[0] : null;
  const sample = first?.starts_at ? first : PREVIEW_SAMPLE_SESSION;
  const tz = sample.timezone?.trim() || PREVIEW_DEFAULT_TZ;

  let sessionTimeFields;
  if (multiZone) {
    const merges = sessionTimeMergeFields(sample.starts_at, sample.ends_at, tz);
    sessionTimeFields = {
      ...merges.fields,
      [PREVIEW_SESSION_TIME_BLOCKS]: { html: merges.sessionTimeHtml, text: merges.sessionTimeText },
    };
  } else {
    sessionTimeFields = {
      session_date: formatPreviewSessionDate(sample.starts_at, tz),
      session_time: formatPreviewSessionTime(sample.starts_at, tz),
    };
  }

  const meetingUrl = String(first?.meeting_url || '').trim();
  const locationText = first?.location_text || '';
  let session_location = meetingUrl || locationText;
  if (!session_location) {
    session_location = event?.format === 'online'
      ? 'https://zoom.us/j/123456789'
      : '123 Main St, Suite 100';
  }

  const inviter_name = 'Alex Rivera';
  const md_name = 'Jordan Lee';
  return {
    first_name: 'Jane',
    event_title: String(event?.title || '').trim() || 'Your Event',
    ...sessionTimeFields,
    session_location,
    session_title: String(first?.title || '').trim() || 'Session 1',
    session_meeting_url: meetingUrl || (event?.format === 'online' ? 'https://zoom.us/j/123456789' : ''),
    session_time_zones: '7:00 PM ET · 6:00 PM CT · 4:00 PM PT',
    session_list: `${String(first?.title || '').trim() || 'Session 1'} — ${formatPreviewSessionDate(sample.starts_at, tz)} — 7:00 PM ET · 6:00 PM CT · 4:00 PM PT`,
    paid_session_details: buildPreviewPaidSessionDetails(sessions, session_location),
    inviter_name,
    inviter_title: 'Financial Professional',
    inviter_headshot_url: fallbackInviterHeadshotUrl(inviter_name),
    inviter_email: 'alex@prosperityleaders.net',
    md_name,
    md_title: 'Marketing Director',
    md_headshot_url: fallbackInviterHeadshotUrl(md_name),
  };
}

function stripInvitedByBlock(template) {
  const start = template.indexOf(INVITED_BY_START);
  if (start === -1) return template;
  const end = template.indexOf(INVITED_BY_END, start);
  if (end === -1) return template;
  return template.slice(0, start) + template.slice(end + INVITED_BY_END.length);
}

function stripInvitedByMarkers(template) {
  return template.split(INVITED_BY_START).join('').split(INVITED_BY_END).join('');
}

function stripMdMarkers(template) {
  return template.split(MD_START).join('').split(MD_END).join('');
}

function applyMergeFields(template, fields) {
  let out = template;
  for (const [key, value] of Object.entries(fields)) {
    out = out.split(`{{${key}}}`).join(value ?? '');
  }
  return out;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Client-side mirror of Edge Function merge + INVITED_BY / MD handling for the Emails-tab preview.
 * Does not import the Deno helper (different runtime).
 *
 * @param {string} template subject or body
 * @param {Record<string, string>} sampleValues from buildPreviewMergeValues
 * @param {{ showInviterCard?: boolean, html?: boolean }} [options]
 *   showInviterCard: keep the invited-by card (default true); false strips the block
 *   html: escape values and linkify meeting URLs like the send path
 */
export function applyPmEmailPreviewMerges(template, sampleValues, options = {}) {
  const showInviterCard = options.showInviterCard !== false;
  const asHtml = Boolean(options.html);
  let out = String(template || '');

  if (showInviterCard) {
    out = stripInvitedByMarkers(out);
  } else {
    out = stripInvitedByBlock(out);
  }
  // Preview always keeps the MD card content (sample MD values); strip only the markers.
  out = stripMdMarkers(out);

  const { [PREVIEW_SESSION_TIME_BLOCKS]: sessionTimeBlocks, ...fields } = sampleValues || {};
  if (!showInviterCard) {
    fields.inviter_name = '';
    fields.inviter_title = '';
    fields.inviter_headshot_url = '';
    fields.inviter_email = '';
  }

  if (!asHtml) {
    return applyMergeFields(out, fields);
  }

  const htmlFields = {};
  for (const [key, value] of Object.entries(fields)) {
    htmlFields[key] = escapeHtml(value);
  }
  const location = String(fields.session_location || '');
  if (/^https?:\/\//i.test(location)) {
    const escaped = escapeHtml(location);
    htmlFields.session_location = `<a href="${escaped}">${escaped}</a>`;
  }
  if (fields.paid_session_details) {
    htmlFields.paid_session_details = escapeHtml(fields.paid_session_details)
      .replace(/\r\n|\r|\n/g, '<br>');
  }

  const isHtmlBody = /<[a-z][\s\S]*>/i.test(out);
  if (sessionTimeBlocks) {
    htmlFields.session_time = isHtmlBody ? sessionTimeBlocks.html : escapeHtml(sessionTimeBlocks.text);
  }
  if (isHtmlBody) {
    return applyMergeFields(out, htmlFields);
  }
  return applyMergeFields(escapeHtml(out), htmlFields).replace(/\r\n|\r|\n/g, '<br>');
}
